import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { SessionProvider, useSession } from './context/SessionContext';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider } from './context/BrandingContext';
import { CustomDomainProvider, useCustomDomainContext } from './context/CustomDomainContext';
import { LiveAnnouncerProvider } from './context/LiveAnnouncerContext';
import { NotificationPrompt } from './components/NotificationPrompt';
import BottomNav from './components/BottomNav';
import InstallPrompt from './components/InstallPrompt';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { useTranslation } from 'react-i18next';
import { migrateLocalStorageKeys } from './utils/storage';
import ScanPage from './pages/ScanPage';
import MenuPage from './pages/MenuPage';
import ModePickerPage from './pages/ModePickerPage';
import DeliveryAddressPage from './pages/DeliveryAddressPage';
import OrdersPage from './pages/OrdersPage';
import AccountPage from './pages/AccountPage';
import AppUnavailable from './components/AppUnavailable';
import './App.css';

// Run one-time localStorage key migration (un-prefixed → carta_ prefixed)
migrateLocalStorageKeys();

// Wrapper that gates takeaway mode based on feature flags
// Falls back to dine-in MenuPage when takeaway is disabled
function TakeawayMenuPage() {
  const flags = useFeatureFlags();
  if (!flags.takeawayEnabled) {
    return <MenuPage mode="dine_in" />;
  }
  return <MenuPage mode="takeaway" />;
}

// Wrapper that gates delivery mode based on feature flags
function DeliveryMenuPage() {
  const flags = useFeatureFlags();
  if (!flags.deliveryEnabled) {
    return <MenuPage mode="takeaway" />;
  }
  return <MenuPage mode="delivery" />;
}

// Wrapper that checks customerAppEnabled before rendering menu routes
// ScanPage is always accessible; menu/orders routes are gated
function GatedRoute({ children }) {
  const flags = useFeatureFlags();
  if (!flags.customerAppEnabled) {
    return <AppUnavailable />;
  }
  return children;
}

// Componente interno que tiene acceso al SessionContext
function AppContent() {
  const { session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { restaurantId: domainRestaurantId } = useCustomDomainContext();

  // Resolve the active restaurantId for branding from the URL FIRST, then custom
  // domain, then the persisted session. The branding fetch must work for a fresh
  // guest landing on /r/{rid} (mode picker / delivery) BEFORE any session exists —
  // mirrors MenuPage's own paramRestaurantId || domainRestaurantId resolution. (NER-1319)
  const routeMatch =
    matchPath('/r/:restaurantId/*', location.pathname) ||
    matchPath('/r/:restaurantId', location.pathname);
  const urlRestaurantId = routeMatch?.params?.restaurantId || null;
  const brandingRestaurantId = urlRestaurantId || domainRestaurantId || session?.restaurantId || null;

  // Hook de Push Notifications
  const pushNotifications = usePushNotifications(
    session?.restaurantId,
    session?.sessionId,
    session?.tableId
  );

  // Hook de Install Prompt (PWA)
  const installPrompt = useInstallPrompt();

  // Determine active tab from current route
  const activeTab = location.pathname === '/orders'
    ? 'orders'
    : location.pathname === '/account'
      ? 'account'
      : 'menu';

  // Show BottomNav only when a session is active (not on ScanPage)
  const showBottomNav = !!session;

  const handleTabChange = (tabId) => {
    if (tabId === 'menu') {
      // Navigate back to the menu route stored in session
      let menuPath;
      if (session?.slug) {
        menuPath = session.tableNumber
          ? `/${session.slug}/mesa/${session.tableNumber}`
          : `/${session.slug}/llevar`;
      } else if (session?.orderMode === 'delivery') {
        menuPath = `/r/${session.restaurantId}/delivery`;
      } else {
        menuPath = session?.tableNumber
          ? `/r/${session.restaurantId}/t/${session.tableNumber}`
          : `/r/${session?.restaurantId}/takeaway`;
      }
      navigate(menuPath);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <BrandingProvider restaurantId={brandingRestaurantId}>
      <main className="App">
        <a href="#main-content" className="skip-link">
          {t('common.skipToContent')}
        </a>
        {/* PWA Install Prompt */}
        <InstallPrompt
          canInstall={installPrompt.canInstall}
          isInstalling={installPrompt.isInstalling}
          onInstall={installPrompt.promptInstall}
          onDismiss={installPrompt.dismissPrompt}
        />

        {/* Solo mostrar prompt si hay una sesión activa */}
        {session && (
          <NotificationPrompt
            isSupported={pushNotifications.isSupported}
            isSubscribed={pushNotifications.isSubscribed}
            permission={pushNotifications.permission}
            isLoading={pushNotifications.isLoading}
            error={pushNotifications.error}
            onSubscribe={pushNotifications.subscribe}
          />
        )}
        <div id="main-content" tabIndex="-1">
        <Routes>
          <Route path="/" element={<ScanPage />} />
          <Route path="/r/:restaurantId" element={<GatedRoute><ModePickerPage /></GatedRoute>} />
          <Route path="/r/:restaurantId/delivery-address" element={<GatedRoute><DeliveryAddressPage /></GatedRoute>} />
          <Route path="/r/:restaurantId/t/:tableNumber" element={<GatedRoute><MenuPage /></GatedRoute>} />
          <Route path="/r/:restaurantId/takeaway" element={<GatedRoute><TakeawayMenuPage /></GatedRoute>} />
          <Route path="/r/:restaurantId/delivery" element={<GatedRoute><DeliveryMenuPage /></GatedRoute>} />
          <Route path="/t/:tableNumber" element={<GatedRoute><MenuPage /></GatedRoute>} />
          <Route path="/:slug" element={<GatedRoute><ModePickerPage /></GatedRoute>} />
          <Route path="/:slug/mesa/:tableNumber" element={<GatedRoute><MenuPage /></GatedRoute>} />
          <Route path="/:slug/llevar" element={<GatedRoute><TakeawayMenuPage /></GatedRoute>} />
          <Route path="/:slug/delivery" element={<GatedRoute><DeliveryMenuPage /></GatedRoute>} />
          <Route path="/orders" element={<GatedRoute><OrdersPage /></GatedRoute>} />
          <Route path="/account" element={<AccountPage />} />
        </Routes>
        </div>

        {showBottomNav && (
          <BottomNav
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onOrdersClick={() => navigate('/orders')}
            onAccountClick={() => navigate('/account')}
          />
        )}
      </main>
    </BrandingProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router basename="/resto/carta">
        <CustomDomainProvider>
          <SessionProvider>
            <FavoritesProvider>
              <CartProvider>
                <LiveAnnouncerProvider>
                  <AppContent />
                </LiveAnnouncerProvider>
              </CartProvider>
            </FavoritesProvider>
          </SessionProvider>
        </CustomDomainProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
